import React from 'react';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { focusRing, hitArea } from './utils/a11y';

/**
 * Alert Component
 * 
 * Display important messages with different severity levels.
 * 
 * @example
 * ```tsx
 * <Alert variant="success" title="Success!">
 *   Your changes have been saved.
 * </Alert>
 * ```
 */

export interface AlertProps {
  /** Alert variant */
  variant?: 'info' | 'success' | 'warning' | 'error';
  /** Alert title */
  title?: string;
  /** Alert message (children) */
  children?: React.ReactNode;
  /** Show icon */
  showIcon?: boolean;
  /** Dismissible */
  dismissible?: boolean;
  /** On dismiss callback */
  onDismiss?: () => void;
  /** Additional className */
  className?: string;
}

const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  children,
  showIcon = true,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  const [visible, setVisible] = React.useState(true);

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  if (!visible) return null;

  // Variant styles
  const variantStyles = {
    info: {
      container: 'bg-info-50 border-info-200 dark:bg-info-900/20 dark:border-info-800/30',
      icon: 'text-info-500',
      title: 'text-info-900 dark:text-info-100',
      text: 'text-info-700 dark:text-info-200',
      Icon: Info,
      role: 'status' as const,
    },
    success: {
      container: 'bg-success-50 border-success-200 dark:bg-success-900/20 dark:border-success-800/30',
      icon: 'text-success-500',
      title: 'text-success-900 dark:text-success-100',
      text: 'text-success-700 dark:text-success-200',
      Icon: CheckCircle2,
      role: 'status' as const,
    },
    warning: {
      container: 'bg-warning-50 border-warning-200 dark:bg-warning-900/20 dark:border-warning-800/30',
      icon: 'text-warning-500',
      title: 'text-warning-900 dark:text-warning-100',
      text: 'text-warning-700 dark:text-warning-200',
      Icon: AlertCircle,
      role: 'alert' as const,
    },
    error: {
      container: 'bg-error-50 border-error-200 dark:bg-error-900/20 dark:border-error-800/30',
      icon: 'text-error-500',
      title: 'text-error-900 dark:text-error-100',
      text: 'text-error-700 dark:text-error-200',
      Icon: AlertCircle,
      role: 'alert' as const,
    },
  };

  const styles = variantStyles[variant];
  const IconComponent = styles.Icon;

  return (
    <div
      className={`rounded-lg border p-4 ${styles.container} ${className}`}
      role={styles.role}
    >
      <div className="flex gap-3">
        {/* Icon */}
        {showIcon && (
          <div className={`flex-shrink-0 ${styles.icon}`}>
            <IconComponent size={20} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className={`text-sm font-semibold mb-1 ${styles.title}`}>
              {title}
            </h4>
          )}
          {children && (
            <div className={`text-sm ${styles.text}`}>
              {children}
            </div>
          )}
        </div>

        {/* Dismiss Button */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            className={`flex-shrink-0 ${styles.icon} hover:opacity-70 transition-opacity rounded ${focusRing} ${hitArea}`}
            aria-label="Dismiss alert"
          >
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

Alert.displayName = 'Alert';

export default Alert;
